import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import ImageCrop, { AspectRatio } from '@/components/atoms/ImageCrop/ImageCrop';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { ExtraUploadConfig } from '@/hooks/useFileUpload';
import { popModal } from '@/redux/modals/actions';

type Props = {
  onEdit: (data: { avatar: Blob | undefined }) => void;
  image: string;
  aspectRatio?: AspectRatio;
  extraConfig?: ExtraUploadConfig;
};

const ModalImageCrop = ({ onEdit, image, aspectRatio, extraConfig }: Props) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [open, setOpen] = useState(true);
  const [croppedImage, setCroppedImage] = useState<Blob | undefined>();

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      setOpen(isOpen);
      if (!isOpen) {
        dispatch(popModal());
      }
    },
    [dispatch]
  );

  const onImageCrop = useCallback((newImage: Blob) => {
    setCroppedImage(newImage);
  }, []);

  const onSubmit = useCallback(() => {
    const data = { avatar: croppedImage };
    try {
      onEdit(data);
    } catch (error) {
      console.error(error);
      return;
    }
    handleOpenChange(false);
  }, [croppedImage, onEdit, handleOpenChange]);

  const primaryButtonText = extraConfig?.primaryButtonText ?? t('common.save');

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-auto sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('modals.image_crop.resize_image')}</DialogTitle>
        </DialogHeader>

        <div className="flex w-full items-center justify-center py-4">
          <ImageCrop file={image} onUpload={onImageCrop} aspectRatio={aspectRatio} />
        </div>
        <DialogFooter>
          <Button variant="ghost-neutral" onClick={() => handleOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={onSubmit}>{primaryButtonText}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ModalImageCrop;
